import {
  coreExtensionData,
  createExtensionBlueprint,
  createExtensionDataRef,
  ExtensionBoundary,
} from '@backstage/frontend-plugin-api';

/**
 * Les points d'attache des deux pages du noyau qui affichent des données de
 * modules : le tableau de bord et « Mon travail ».
 *
 * Même mécanique que les cartes de la vue d'ensemble d'une entité
 * (`EntityCardBlueprint`) : la page appartient au noyau, les widgets
 * appartiennent aux modules. Un client sans le module `dora` n'a pas de
 * widget DORA sur son tableau de bord — au lieu d'un widget en erreur 404,
 * ce que le profil client minimal a montré (phase 5).
 *
 * Les identifiants `page:platform/dashboard` et `page:platform/my-work` sont le
 * contrat : ce sont les pages déclarées par le socle
 * `@platform/plugin-platform`. Les widgets s'affichent dans l'ordre de déclaration de
 * `app.extensions`, puis dans l'ordre de découverte des plugins.
 */
/** Colonne du tableau de bord : principale (gauche) ou latérale (droite). */
export type DashboardWidgetArea = 'main' | 'side';

export const dashboardWidgetAreaDataRef =
  createExtensionDataRef<DashboardWidgetArea>().with({
    id: 'platform.dashboard-widget.area',
  });

export const DashboardWidgetBlueprint = createExtensionBlueprint({
  kind: 'dashboard-widget',
  attachTo: { id: 'page:platform/dashboard', input: 'widgets' },
  output: [coreExtensionData.reactElement, dashboardWidgetAreaDataRef],
  dataRefs: { area: dashboardWidgetAreaDataRef },
  factory(
    params: { area?: DashboardWidgetArea; loader: () => Promise<JSX.Element> },
    { node },
  ) {
    return [
      coreExtensionData.reactElement(ExtensionBoundary.lazy(node, params.loader)),
      dashboardWidgetAreaDataRef(params.area ?? 'main'),
    ];
  },
});

export type MyWorkCardWidth = 'full' | 'wide' | 'narrow';

export const myWorkCardWidthDataRef =
  createExtensionDataRef<MyWorkCardWidth>().with({
    id: 'platform.my-work-card.width',
  });

/** Une carte de la page « Mon travail ». Voir `DashboardWidgetBlueprint`. */
export const MyWorkCardBlueprint = createExtensionBlueprint({
  kind: 'my-work-card',
  attachTo: { id: 'page:platform/my-work', input: 'cards' },
  output: [coreExtensionData.reactElement, myWorkCardWidthDataRef],
  dataRefs: { width: myWorkCardWidthDataRef },
  factory(
    params: { width: MyWorkCardWidth; loader: () => Promise<JSX.Element> },
    { node },
  ) {
    return [
      coreExtensionData.reactElement(ExtensionBoundary.lazy(node, params.loader)),
      myWorkCardWidthDataRef(params.width),
    ];
  },
});
